import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, MapPin } from "lucide-react";
import { inr } from "@/utils";

export default function Projects() {
  const { data: projects = [], isLoading } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => (await api.get("/projects")).data,
  });

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-emerald-700">Projects</div>
          <h1 className="font-heading text-3xl font-bold mt-1">All Projects</h1>
          <p className="text-sm text-zinc-500 mt-1">Every project maintains its own ledger, inventory, customers and P&amp;L.</p>
        </div>
        <Link to="/projects/new">
          <Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="create-project-btn">
            <Plus size={14} className="mr-1" /> New Project
          </Button>
        </Link>
      </div>

      {isLoading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : projects.length === 0 ? (
        <Card className="border-dashed border-zinc-300 bg-white">
          <CardContent className="p-16 text-center">
            <div className="text-lg font-heading font-semibold">No projects yet</div>
            <p className="text-sm text-zinc-500 mt-2">Create your first project to auto-provision its ledger, inventory and dashboards.</p>
            <Link to="/projects/new" className="inline-block mt-4">
              <Button className="bg-emerald-700 hover:bg-emerald-800">Create project</Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {projects.map((p) => {
            const s = p.unit_stats || {};
            const sold = (s.booked || 0) + (s.agreement_done || 0) + (s.registered || 0) + (s.sold || 0);
            const pct = s.total ? Math.round((sold / s.total) * 100) : 0;
            return (
              <Link key={p.id} to={`/projects/${p.id}`} data-testid={`project-card-${p.code}`}>
                <Card className="overflow-hidden border-zinc-200 hover:border-emerald-400 hover:shadow-md transition-shadow bg-white h-full">
                  <div className="aspect-[16/9] bg-zinc-100 relative">
                    {p.thumbnail_url && <img src={p.thumbnail_url + (p.updated_at ? `?v=${encodeURIComponent(p.updated_at)}` : "")} alt="" className="w-full h-full object-cover" />}
                    <div className="absolute top-3 left-3 flex gap-1.5">
                      <span className="bg-white/95 text-[10px] uppercase tracking-widest font-mono font-semibold px-2 py-1 rounded">{p.code}</span>
                      {p.ownership_type === "jda" && (
                        <span className="bg-amber-500 text-white text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded">JDA</span>
                      )}
                    </div>
                  </div>
                  <CardContent className="p-5 space-y-3">
                    <div>
                      <div className="font-heading text-lg font-semibold leading-tight">{p.name}</div>
                      <div className="text-xs text-zinc-500 flex items-center gap-1 mt-1">
                        <MapPin size={11} /> {p.location}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Est. Revenue</div>
                        <div className="font-mono font-semibold mt-0.5">{inr(p.estimated_revenue)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Est. Cost</div>
                        <div className="font-mono font-semibold mt-0.5">{inr(p.estimated_cost)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Units</div>
                        <div className="font-mono font-semibold mt-0.5">{s.total || 0}</div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-[11px] text-zinc-500">
                        <span>Inventory sold</span>
                        <span className="font-mono">{sold}/{s.total || 0} · {pct}%</span>
                      </div>
                      <div className="mt-1 h-1.5 bg-zinc-100 rounded">
                        <div className="h-1.5 bg-emerald-600 rounded" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
